import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  ProgressBar,
  Tag,
  Tooltip,
  makeStyles,
} from '@fluentui/react-components';
import { ArrowSync24Regular, BrainCircuit24Regular } from '@fluentui/react-icons';
import type { ProjectIndexStatus } from '../api/projects.js';
import { reindexProject } from '../api/rag.js';
import { stopIndexing } from '../api/projects.js';

interface Props {
  projectId: string;
  status: ProjectIndexStatus;
  chunksTotal: number;
  chunksEmbedded: number;
  /** Algún archivo aún en parseStatus distinto de 'ok' o 'failed' */
  parsingInFlight: boolean;
  onReindexed?: () => void;
  /** Si es false, deshabilita "Volver a analizar" (proyecto de ejemplo/solo lectura). */
  canReindex?: boolean;
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-s)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 'var(--space-s)',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-xs)',
    fontFamily: 'var(--font-mono)',
    fontSize: 'var(--fs-200)',
    color: 'var(--text-tertiary)',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  },
  meta: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 'var(--fs-200)',
    color: 'var(--text-secondary)',
    fontFamily: 'var(--font-mono)',
  },
});

const STATUS_LABEL: Record<ProjectIndexStatus, string> = {
  idle: 'sin procesar',
  chunking: 'preparando',
  embedding: 'analizando',
  partial: 'incompleto',
  ready: 'listo',
  failed: 'error',
};

const STATUS_COLOR: Record<ProjectIndexStatus, string> = {
  idle: 'var(--text-tertiary)',
  chunking: 'var(--semantic-warning)',
  embedding: 'var(--semantic-warning)',
  partial: 'var(--semantic-warning)',
  ready: 'var(--semantic-success)',
  failed: 'var(--semantic-danger)',
};

export function IndexProgress({
  projectId,
  status,
  chunksTotal,
  chunksEmbedded,
  parsingInFlight,
  onReindexed,
  canReindex = true,
}: Props) {
  const styles = useStyles();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // ALTO de la lectura. Confirmación obligatoria, como en todos los Altos.
  const [confirmarAlto, setConfirmarAlto] = useState(false);
  const [deteniendo, setDeteniendo] = useState(false);
  async function onStop() {
    setConfirmarAlto(false);
    setDeteniendo(true);
    try {
      await stopIndexing(projectId);
      onReindexed?.(); // refresca el estado del proyecto tras la limpieza
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'No se pudo detener');
    } finally {
      setDeteniendo(false);
    }
  }

  // Durante el PARSEO (leer el archivo: parser/OCR/LibreOffice) todavía no hay
  // chunks ni status de índice → sin esto la barra quedaba en 0% y "Sin
  // documentos", pareciendo colgada aunque SÍ está leyendo. Barra indeterminada.
  const indeterminate =
    parsingInFlight || ((status === 'chunking' || status === 'embedding') && chunksTotal === 0);
  // Clamp: durante el embedding el worker a veces manda chunksEmbedded
  // actualizado sin refrescar chunksTotal → el ratio se iba a 137%/243%.
  const progress =
    chunksTotal > 0 ? Math.min(1, Math.max(0, chunksEmbedded / chunksTotal)) : 0;

  async function onReindex() {
    setBusy(true);
    setErr(null);
    try {
      await reindexProject(projectId);
      onReindexed?.();
    } catch (e) {
      setErr((e as Error).message ?? 'Error reindex');
    } finally {
      setBusy(false);
    }
  }

  const reindexDisabled = !canReindex || busy || parsingInFlight || status === 'chunking' || status === 'embedding';

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <span className={styles.title}>
          <BrainCircuit24Regular fontSize={14} /> Búsqueda en documentos
        </span>
        {/* El pill "listo" parecía botón y no aporta cuando todo está bien
            (reunión 2026-07-02); solo se muestra en estados informativos. */}
        {(parsingInFlight || status !== 'ready') && (
          <Tag
            size="extra-small"
            appearance="filled"
            style={{
              color: parsingInFlight ? 'var(--semantic-warning)' : STATUS_COLOR[status],
              borderColor: parsingInFlight ? 'var(--semantic-warning)' : STATUS_COLOR[status],
            }}
          >
            {parsingInFlight ? 'leyendo' : STATUS_LABEL[status]}
          </Tag>
        )}
      </div>

      {indeterminate ? (
        <ProgressBar />
      ) : (
        <ProgressBar value={progress} max={1} thickness="medium" />
      )}

      <div className={styles.meta}>
        <span>
          {deteniendo
            ? 'Deteniendo la lectura de documentos…'
            : parsingInFlight
            ? 'Leyendo documento…'
            : status === 'ready'
            ? 'Documentos listos para búsqueda'
            : status === 'chunking'
            ? 'Preparando documentos…'
            : status === 'embedding'
            ? 'Analizando documentos…'
            : status === 'partial'
            ? 'Análisis incompleto'
            : status === 'failed'
            ? 'Error en el análisis'
            : 'Sin documentos'}
        </span>
        <span>{chunksTotal > 0 ? `${(progress * 100).toFixed(0)}%` : ''}</span>
      </div>

      {/* ALTO de la lectura (reporte de dirección). Con confirmación, como todos los Altos:
          esa pausa también le da al worker unos segundos para cerrar lo que está leyendo. */}
      {(parsingInFlight || status === 'chunking' || status === 'embedding') && (
        <Button
          appearance="subtle"
          size="small"
          disabled={deteniendo}
          onClick={() => setConfirmarAlto(true)}
          title="Detener la lectura de los documentos del proyecto"
        >
          {/* El botón dice QUÉ detiene: en esta pantalla puede haber otro Alto a la vista
              (el del resumen), y "Detener" solo no distingue (pedido de Sebastián, 2026-08-12). */}
          {deteniendo ? 'Deteniendo la lectura…' : 'Detener la lectura de documentos'}
        </Button>
      )}
      <Dialog open={confirmarAlto} onOpenChange={(_, d) => { if (!d.open) setConfirmarAlto(false); }}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>¿Detener la lectura de los documentos?</DialogTitle>
            <DialogContent>
              <p style={{ margin: 0 }}>
                Los documentos que quedaron a mitad <strong>no se guardan a medias</strong>:
                vuelven a quedar pendientes, porque un documento leído por la mitad parece
                listo y se usaría para buscar. Los que ya terminaron quedan listos. Puedes
                volver a leerlos cuando quieras.
              </p>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setConfirmarAlto(false)}>Seguir trabajando</Button>
              <Button appearance="primary" onClick={() => void onStop()}>Sí, detener</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <Tooltip
        content={
          parsingInFlight
            ? 'Espera a que todos los documentos terminen de leerse.'
            : 'Vuelve a analizar todos los documentos del proyecto para actualizar la búsqueda.'
        }
        relationship="description"
      >
        <Button
          appearance="subtle"
          icon={<ArrowSync24Regular />}
          size="small"
          disabled={reindexDisabled}
          onClick={() => void onReindex()}
        >
          {busy ? 'Procesando…' : 'Volver a analizar'}
        </Button>
      </Tooltip>

      {err && (
        <span style={{ color: 'var(--semantic-danger)', fontSize: 'var(--fs-200)' }}>{err}</span>
      )}
    </div>
  );
}
